/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AbstractControl, NgControl, ValidatorFn } from '@angular/forms';
import { concat, of, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

function processString(input: string, value?) {
	return input.replace('{val}', value);
}

// component migration for errors
// - create or use validator
// - add validator to formBuilder for the component
// - change controlValueAccessor provider to the manual configuration method
// from:
// providers: [
// 	{
// 		provide: NG_VALUE_ACCESSOR,
// 		useExisting: forwardRef(() => TicketDurationComponent),
// 		multi: true
// 	}
// ]
// to:
// constructor(@Self() @Optional() public control: NgControl){
// 	this.control && (this.control.valueAccessor = this);
//
// - (interiem) add errorState observable in ngOnInit
// formErrorMapping(this.control).pipe( takeUntil(this.shutdownSubject) ).subscribe(this._errorState);
// - create a class for styling the input itself
// `ticket-description.ng-invalid textarea`

export interface TicketError {
	value: any /** value of the control at time of error */;
	messageHeader: string /** static content header */;
	message: string /** static content */;
	//use the dynamic stuff only if you need it. Allows you to build a more specific error message
	// while deferring calculation of that message to render time. Because validators run a lot
	// constantly recalcing the message is bad.
	dynamicMessageHeader?: () => void /** for non-static messages, generally not used */;
	dynamicMessage?: () => void /** for non-static messages, generally not used */;
}

//expects a formControl, returns an observable that emits whenever the form status changes
// with data in the form of an error message and a header
// if there's more than one error, it will pick one. If there are no errors it will emit null (maybe for each value of the object?)
export function formErrorMapping(control: NgControl) {
	return concat(of(control.status), control.statusChanges).pipe(
		map((status) => {
			if (status === 'VALID' || !control.errors) {
				return null;
			}
			const errorKeys = Object.keys(control.errors);
			return control.getError(errorKeys[0]);
		})
	);
}

/** returns error data when the formControl has data */
export function testValidator() {
	return (control: AbstractControl) => {
		return control.value
			? {
					testValidator: {
						value: control.value,
						messageHeader: 'test',
						message: 'woooooooooo',
					},
			  }
			: null;
	};
}

/**
 * A special required validator that considers the keyMetadata of the field when determining if it's actually empty
 * @param  keyMetadata   Observable with the metadata field, it only considers "multiple" at the moment.
 * This observable is required to complete. Pipe infinites through a takeUntil before passing in.
 * @param  messageHeader text
 * @param  message       text
 */
export function requiredOrMulti(keyMetadata: Observable<string>, messageHeader?: string, message?: string) {
	if (!messageHeader) {
		messageHeader = 'Unexpected Input Value';
	}
	if (!message) {
		message = 'Value is required.';
	}

	let syncMetadata = null;
	keyMetadata.subscribe((data) => (syncMetadata = data));

	return (control: AbstractControl) => {
		return (control.value === '' || control.value === null || control.value === undefined) &&
			syncMetadata !== 'multiple'
			? {
					requiredOrMulti: {
						value: control.value,
						messageHeader,
						message,
					},
			  }
			: null;
	};
}

/**
 * returns error data when the formControl is less than the number provided.
 * Empty is considered a valid state, if you don't want, also use a required
 */
export function greaterThanValidator(num: number, messageOverride?: string) {
	const message = messageOverride || `The number must be at least ${num}.`;
	return (control: AbstractControl) => {
		if (control.value === null || control.value === undefined || control.value === '') {
			return null;
		}

		return control.value < num
			? {
					greaterThan: {
						value: control.value,
						messageHeader: 'Unexpected Input Value',
						message,
					},
			  }
			: null;
	};
}

/** returns error data when the formControl date range is null */
export function dateRangeStartRequiredValidator() {
	const message = `A start date must exist for tickets on the active side.`;
	return (control: AbstractControl) => {
		return control.value === null || control.value === undefined || control.value[0] === null
			? {
					dateRangeStartRequired: {
						value: control.value,
						messageHeader: 'Missing Start Date',
						message,
					},
			  }
			: null;
	};
}

/**
 * Wraps an angular built in validator with a set of TicketError data
 * @param  validator     [description]
 * @param  messageHeader HeaderText
 * @param  message       MessageText
 */
export function validatorDecorator(validator: ValidatorFn, messageHeader: string, message: string) {
	return (control: AbstractControl) => {
		const res = validator(control);
		if (!res) {
			return res;
		}
		const newRes: any = {};
		Object.keys(res).forEach((key) => {
			newRes[key] = {
				value: control.value,
				// origRes: res[key],
				messageHeader,
				message,
			};
		});
		return newRes;
	};
}
